import { useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import {
  commissionsApi,
  type AdminPaymentListQuery,
  type AdminPaymentRowDto,
  type RevenuePoolDto,
} from "@/api/commissions";
import { Button, Card, FormField, Input } from "@/design-system/components/ui";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { calendarDate, currency, truncate } from "@/components/commissions/formatters";

const PAGE_SIZE = 50;

export function AdminPaymentLedgerPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useMemo<AdminPaymentListQuery>(() => ({
    page: parseInt(searchParams.get("page") ?? "1", 10) || 1,
    pageSize: PAGE_SIZE,
    poolIds: searchParams.get("poolIds") || undefined,
    claimantId: searchParams.get("claimantId") || undefined,
    dateFrom: searchParams.get("dateFrom") || undefined,
    dateTo: searchParams.get("dateTo") || undefined,
    minAmount: parseDecimalParam(searchParams.get("minAmount")),
    maxAmount: parseDecimalParam(searchParams.get("maxAmount")),
    q: searchParams.get("q") || undefined,
  }), [searchParams]);

  const payments = useQuery({
    queryKey: commissionKeys.adminPaymentList(query),
    queryFn: () => commissionsApi.adminListPayments(query),
  });

  const aggregates = useQuery({
    queryKey: commissionKeys.adminPaymentAggregates(query),
    queryFn: () => commissionsApi.adminPaymentAggregates(query),
  });

  const pools = useQuery({
    queryKey: commissionKeys.poolList("all"),
    queryFn: () => commissionsApi.listPools(true),
  });

  const setParam = (key: string, value: string | undefined) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === undefined || value === "") next.delete(key);
      else next.set(key, value);
      if (key !== "page") next.delete("page");
      return next;
    });
  };

  const togglePool = (poolId: string) => {
    const current = (query.poolIds ?? "").split(",").filter(Boolean);
    const next = current.includes(poolId)
      ? current.filter(p => p !== poolId)
      : [...current, poolId];
    setParam("poolIds", next.length > 0 ? next.join(",") : undefined);
  };

  // The text, date and amount inputs are uncontrolled, so clearing the URL
  // left them showing the old values, and the next blur applied them again.
  // They are emptied here, as on All Claims; see the note there on why a
  // remount with a new key cannot do it.
  const filterInputsRef = useRef<HTMLDivElement>(null);
  const clearFilters = () => {
    filterInputsRef.current?.querySelectorAll("input").forEach((input) => {
      input.value = "";
    });
    setSearchParams(new URLSearchParams());
  };

  const selectedPoolIds = (query.poolIds ?? "").split(",").filter(Boolean);

  const totalPages = Math.max(1, Math.ceil((payments.data?.totalCount ?? 0) / PAGE_SIZE));
  const currentPage = query.page ?? 1;

  return (
    <div className="flex flex-col gap-space-5">
      <div className="flex items-end justify-between gap-space-3">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            Commissions · Admin
          </div>
          <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0">
            Payment ledger
          </h1>
          <p className="text-body text-gray-mid mt-space-2 max-w-reading">
            Every recorded commission payment, including adjustments and clawbacks. YTD and
            MTD tiles describe the firm; the filtered total reflects the current view.
          </p>
        </div>
        <a
          href={commissionsApi.adminPaymentsExportUrl(query)}
          download
          className="inline-flex"
        >
          <Button variant="secondary">
            <Download size={14} strokeWidth={2} aria-hidden /> Export CSV
          </Button>
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3">
        <Tile label="Total Paid YTD" value={currency(aggregates.data?.totalPaidYtd ?? 0)} />
        <Tile label="Total Paid MTD" value={currency(aggregates.data?.totalPaidMtd ?? 0)} />
        <Tile
          label="Total Paid (filtered)"
          value={currency(aggregates.data?.totalPaidFiltered ?? 0)}
          subtext={`${aggregates.data?.filteredCount ?? 0} payment${aggregates.data?.filteredCount === 1 ? "" : "s"}`}
        />
      </div>

      {aggregates.data && aggregates.data.perPool.length > 0 && (
        <Card>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gray-mid mb-space-2">
            Per-pool subtotals (filtered view)
          </div>
          <div className="flex flex-wrap gap-space-2">
            {aggregates.data.perPool.map(p => (
              <div
                key={p.poolId}
                className="px-space-3 py-space-2 border border-border-light rounded-md bg-white-warm"
              >
                <span className="font-display text-label font-semibold tracking-label text-gray-mid">
                  {p.poolCode}
                </span>{" "}
                <span className="text-onyx">{p.poolName}</span>{" "}
                <span className="font-semibold tabular-nums text-gold-shadow">
                  {currency(p.total)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="flex flex-col gap-space-3">
        <div ref={filterInputsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-space-3">
          <FormField label="Search" htmlFor="q">
            <Input
              id="q"
              defaultValue={query.q ?? ""}
              placeholder="Payment #, reference, claim #"
              onBlur={(e) => setParam("q", e.target.value || undefined)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setParam("q", (e.target as HTMLInputElement).value || undefined);
              }}
            />
          </FormField>
          <FormField label="Claimant user id" htmlFor="claimantId">
            <Input
              id="claimantId"
              defaultValue={query.claimantId ?? ""}
              placeholder="User id"
              onBlur={(e) => setParam("claimantId", e.target.value || undefined)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setParam("claimantId", (e.target as HTMLInputElement).value || undefined);
              }}
            />
          </FormField>
          <FormField label="Payment date from" htmlFor="dateFrom">
            <Input
              id="dateFrom"
              type="date"
              defaultValue={query.dateFrom?.slice(0, 10) ?? ""}
              onBlur={(e) => setParam("dateFrom", e.target.value || undefined)}
            />
          </FormField>
          <FormField label="Payment date to" htmlFor="dateTo">
            <Input
              id="dateTo"
              type="date"
              defaultValue={query.dateTo?.slice(0, 10) ?? ""}
              onBlur={(e) => setParam("dateTo", e.target.value || undefined)}
            />
          </FormField>
          <FormField label="Min amount" htmlFor="minAmount">
            <Input
              id="minAmount"
              type="number"
              step="0.01"
              defaultValue={query.minAmount ?? ""}
              onBlur={(e) => setParam("minAmount", e.target.value || undefined)}
            />
          </FormField>
          <FormField label="Max amount" htmlFor="maxAmount">
            <Input
              id="maxAmount"
              type="number"
              step="0.01"
              defaultValue={query.maxAmount ?? ""}
              onBlur={(e) => setParam("maxAmount", e.target.value || undefined)}
            />
          </FormField>
        </div>

        {pools.data && pools.data.length > 0 && (
          <div className="flex flex-col gap-space-2">
            <div className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
              Pool
            </div>
            <div className="flex flex-wrap gap-space-2">
              {pools.data.map((p: RevenuePoolDto) => (
                <FilterChip
                  key={p.id}
                  label={`${p.code} · ${p.name}`}
                  active={selectedPoolIds.includes(p.id)}
                  onClick={() => togglePool(p.id)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="secondary" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      </Card>

      <PaymentsTable rows={payments.data?.data ?? []} loading={payments.isLoading} />

      <div className="flex items-center justify-between text-small text-gray-mid">
        <div>
          {payments.data
            ? `${payments.data.totalCount.toLocaleString()} payment${payments.data.totalCount === 1 ? "" : "s"} · page ${currentPage} of ${totalPages}`
            : "—"}
        </div>
        <div className="flex gap-space-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setParam("page", String(currentPage - 1))}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => setParam("page", String(currentPage + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

function parseDecimalParam(value: string | null): number | undefined {
  if (value === null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function Tile({
  label, value, subtext,
}: {
  label: string;
  value: string;
  subtext?: string;
}) {
  return (
    <Card>
      <div className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
        {label}
      </div>
      <div className="font-display font-bold text-h2 tabular-nums mt-space-2 text-gold-shadow">
        {value}
      </div>
      {subtext && <div className="text-small text-gray-mid mt-space-1">{subtext}</div>}
    </Card>
  );
}

function FilterChip({
  label, active, onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "h-8 px-space-3 font-display text-label font-semibold uppercase tracking-label rounded-md " +
        "border transition-colors duration-fast " +
        (active
          ? "bg-gold text-onyx border-gold"
          : "bg-white-pure text-gray-mid border-border-light hover:border-gold")
      }
    >
      {label}
    </button>
  );
}

function PaymentsTable({ rows, loading }: { rows: AdminPaymentRowDto[]; loading: boolean }) {
  if (loading) {
    return (
      <Card>
        <div className="font-display text-label uppercase tracking-label text-gray-mid">Loading…</div>
      </Card>
    );
  }
  if (rows.length === 0) {
    return (
      <Card>
        <div className="text-center py-space-4">
          <div className="font-display font-bold text-body text-onyx">No payments match these filters.</div>
          <div className="text-small text-gray-mid mt-space-1">Adjust the filter chips or clear them to widen the search.</div>
        </div>
      </Card>
    );
  }
  return (
    <Card className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-small">
          <thead className="bg-onyx">
            <tr>
              <Th label="Payment #" />
              <Th label="Date" />
              <Th label="Claim #" />
              <Th label="Claimant" />
              <Th label="Pool" />
              <Th label="Amount" align="right" />
              <Th label="Method" />
              <Th label="Reference" />
              <Th label="Adjustment" />
              <Th label="Notes" />
              <Th label="Recorded By" />
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-t border-border-light hover:bg-gold-highlight/10 transition-colors duration-fast">
                <td className="px-space-3 py-space-3 font-mono text-onyx">{p.paymentNumber}</td>
                <td className="px-space-3 py-space-3 text-gray-mid">{calendarDate(p.paymentDate)}</td>
                <td className="px-space-3 py-space-3 font-mono">
                  <Link to={`/commissions/${p.commissionClaimId}`} className="text-gold-shadow hover:underline">
                    {p.claimNumber}
                  </Link>
                </td>
                <td className="px-space-3 py-space-3">{p.claimantDisplayName ?? p.claimantUserId}</td>
                <td className="px-space-3 py-space-3">
                  <span className="font-display text-label font-semibold tracking-label text-gray-mid">{p.poolCode}</span>{" "}
                  <span className="text-onyx">{p.poolName}</span>
                </td>
                <td className={
                  "px-space-3 py-space-3 text-right tabular-nums font-semibold " +
                  (p.amount < 0 ? "text-status-error" : "text-onyx")
                }>
                  {currency(p.amount)}
                </td>
                <td className="px-space-3 py-space-3 text-gray-mid">{p.paymentMethod}</td>
                <td className="px-space-3 py-space-3 font-mono text-gray-mid">{p.referenceNumber}</td>
                <td className="px-space-3 py-space-3">
                  {p.adjustmentType ? (
                    <span title={p.adjustmentReason ?? ""}>{p.adjustmentType}</span>
                  ) : "—"}
                </td>
                <td className="px-space-3 py-space-3 text-gray-mid" title={p.notes ?? ""}>
                  {p.notes ? truncate(p.notes, 40) : "—"}
                </td>
                <td className="px-space-3 py-space-3">{p.recordedByDisplayName ?? p.recordedByUserId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Th({ label, align = "left" }: { label: string; align?: "left" | "right" }) {
  return (
    <th className={`px-space-3 py-space-3 font-display text-[10px] font-semibold uppercase tracking-label text-gold ${align === "right" ? "text-right" : "text-left"}`}>
      {label}
    </th>
  );
}
